import React, { useRef, useEffect, useState } from 'react';
import { FaRegClock, FaPiggyBank, FaShieldAlt } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';

const palette = {
  text: '#18171b',
  subtitle: '#702fff',
  buttonBg: '#18171b',
  buttonText: '#fff',
  buttonBorder: '#e5e8f0',
  buttonSecondaryBg: '#fff',
  buttonSecondaryText: '#18171b',
  cardBg: '#fff',
  cardShadow: 'rgba(64,46,122,0.08)',
};

const useCaseSummaries = [
  {
    title: 'Unify All Your Data',
    summary: 'Connect and centralize data from Salesforce, HubSpot, MondayCRM, FreshSales, and more—no more silos, just a single source of truth for your business.',
  },
  {
    title: 'Instant AI-Powered Insights',
    summary: 'Launch dashboards and analytics in hours, not weeks. Get actionable insights and recommendations with built-in AI and automation.',
  },
  {
    title: 'Scale Without Limits',
    summary: 'Empower every team with a secure, governed platform that grows with you. Deploy new dashboards instantly, and automate flows with our AI agents.',
  },
];

const icons = [
  <FaRegClock size={36} color="#f6f6ff" />, // Faster delivery
  <FaPiggyBank size={36} color="#f6f6ff" />, // Lower cost
  <FaShieldAlt size={36} color="#f6f6ff" /> // Higher quality
];

const ShortUseCasesSection = () => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [showTitle, setShowTitle] = useState(false);
  const [showDesc, setShowDesc] = useState(false);
  const [showCards, setShowCards] = useState(false);
  const [cardVisibles, setCardVisibles] = useState(Array(useCaseSummaries.length).fill(false));
  const [showSeeMore, setShowSeeMore] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Intersection Observer for scroll-in animation
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Staggered appearance
  useEffect(() => {
    let titleTimeout, descTimeout, cardsTimeout;
    if (visible) {
      titleTimeout = setTimeout(() => setShowTitle(true), 200);
      descTimeout = setTimeout(() => setShowDesc(true), 500);
      cardsTimeout = setTimeout(() => setShowCards(true), 700);
    }
    return () => {
      clearTimeout(titleTimeout);
      clearTimeout(descTimeout);
      clearTimeout(cardsTimeout);
    };
  }, [visible]);

  useEffect(() => {
    if (showCards) {
      useCaseSummaries.forEach((_, idx) => {
        setTimeout(() => {
          setCardVisibles(prev => {
            const updated = [...prev];
            updated[idx] = true;
            return updated;
          });
        }, idx * 180);
      });
      // Show 'See more' after all cards are visible
      setTimeout(() => setShowSeeMore(true), useCaseSummaries.length * 180 + 300);
    } else {
      setCardVisibles(Array(useCaseSummaries.length).fill(false));
      setShowSeeMore(false);
    }
  }, [showCards]);

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <section ref={sectionRef} style={styles.section}>
      <h2
        style={{
          ...styles.title,
          opacity: showTitle ? 1 : 0,
          transform: showTitle ? 'translateY(0)' : 'translateY(40px)',
          transition: 'opacity 0.7s cubic-bezier(.4,2,.6,1), transform 0.7s cubic-bezier(.4,2,.6,1)'
        }}
      >Connect Your Data and Drive Insights</h2>
      <p
        style={{
          ...styles.desc,
          opacity: showDesc ? 1 : 0,
          transition: 'opacity 0.7s 0.2s cubic-bezier(.4,2,.6,1)'
        }}
      >
        Discover how our platform empowers your business to unify all your data, migrate to the cloud in days, and scale AI-driven insights—so you can deliver business value faster than ever.
      </p>
      <div style={styles.cardsWrap}>
        {useCaseSummaries.map((uc, idx) => (
          <div
            key={uc.title}
            className="short-usecase-card"
            style={{
              ...styles.card,
              opacity: cardVisibles[idx] ? 1 : 0,
              transform: cardVisibles[idx] ? 'translateY(0)' : 'translateY(50px)',
              transition: 'opacity 0.9s cubic-bezier(.4,2,.6,1), transform 0.9s cubic-bezier(.4,2,.6,1)'
            }}
          >
            <div style={styles.iconWrap}>{icons[idx]}</div>
            <div>
              <div style={styles.cardTitle}>{uc.title}</div>
              <div style={styles.cardSummary}>{uc.summary}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={styles.buttonRow}>
        <span
          onClick={() => navigate('/usecases')}
          style={{
            color: '#4b70f5',
            fontWeight: 600,
            fontSize: 18,
            cursor: 'pointer',
            textDecoration: 'none',
            fontFamily: 'Inter, sans-serif',
            letterSpacing: 1.1,
            transition: 'color 0.2s, opacity 0.7s cubic-bezier(.4,2,.6,1), transform 0.7s cubic-bezier(.4,2,.6,1)',
            marginTop: 8,
            marginBottom: 8,
            outline: 'none',
            opacity: showSeeMore ? 1 : 0,
            transform: showSeeMore ? 'translateY(0)' : 'translateY(40px)',
            pointerEvents: showSeeMore ? 'auto' : 'none',
          }}
          tabIndex={0}
          onKeyPress={e => { if (e.key === 'Enter') navigate('/usecases'); }}
        >
          See more
        </span>
      </div>
      {/* Animation styles */}
      <style>{`
        .short-usecase-card:hover {
          transform: translateY(-8px) scale(1.04) rotate(-1deg) !important;
          box-shadow: 0 8px 32px 0 rgba(64,46,122,0.18);
          transition: transform 0.25s cubic-bezier(.4,2,.6,1), box-shadow 0.25s cubic-bezier(.4,2,.6,1);
        }
        @media (max-width: 600px) {
          .short-usecase-card { min-height: 160px; }
        }
        @media (max-width: 700px) {
          .short-usecase-card .iconWrap {
            margin-left: auto !important;
            margin-right: auto !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
        }
      `}</style>
    </section>
  );
};

const styles = {
  section: {
    width: '95%',
    maxWidth: 1200,
    margin: '0 auto',
    padding: '64px 0 0 0',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 400,
    fontSize: 36,
    color: '#18171b',
    margin: '0 0 18px 0',
    lineHeight: 1.15,
    letterSpacing: 0,
    textAlign: 'center',
    maxWidth: 900,
  },
  desc: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 300,
    fontSize: 18,
    color: '#666',
    margin: '0 0 48px 0',
    maxWidth: 800,
    lineHeight: 1.6,
    textAlign: 'center',
  },
  cardsWrap: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: 32,
    width: '100%',
    maxWidth: 1100,
    marginTop: 32,
  },
  card: {
    background: palette.cardBg,
    borderRadius: 18,
    boxShadow: `0 4px 24px 0 ${palette.cardShadow}`,
    padding: '32px 24px 28px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    minHeight: 220,
    transition: 'transform 0.2s cubic-bezier(.4,2,.6,1), box-shadow 0.2s cubic-bezier(.4,2,.6,1)',
    cursor: 'pointer',
    fontFamily: 'Inter, sans-serif',
  },
  iconWrap: {
    marginBottom: 18,
    background: 'linear-gradient(135deg, #b8baff 0%, #6666ff 50%, #7bd7f7 100%)',
    borderRadius: '50%',
    padding: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 2px 8px rgba(64,46,122,0.10)',
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: 20,
    color: palette.text,
    margin: '0 0 10px 0',
    fontFamily: 'Inter, sans-serif',
  },
  cardSummary: {
    fontWeight: 400,
    fontSize: 16,
    color: '#222',
    fontFamily: 'Inter, sans-serif',
    lineHeight: 1.5,
  },
  buttonRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: 24,
    marginTop: 24,
    justifyContent: 'center',
  },
  buttonPrimary: {
    background: '#18171b',
    color: '#fff',
    border: 'none',
    borderRadius: 20,
    fontSize: 22,
    fontWeight: 500,
    padding: '12px 28px',
    cursor: 'pointer',
    fontFamily: 'Inter, sans-serif',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    transition: 'background 0.2s',
  },
};

export default ShortUseCasesSection;
